import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from './api';
import { formatStoreName } from './storeUtils';
import { extractGarmentSuffix } from './displayUtils';

/**
 * Generates an Order Sheet PDF following the exact template structure
 * @param {Object} params - The parameters for PDF generation
 */
export const generateOrderSheetPdf = async ({
  garment,
  order,
  customer,
  lineItem,
  product,
  allCategories = [],
  allOptions = [],
  patternRevision,
  linkedMeasurement,
  specificationFields = [],
  referenceImages = [],
}) => {
  // Create new PDF document (A4 size)
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 8;
  const contentWidth = pageWidth - 2 * margin;
  
  let currentY = margin;

  // Helper: Convert Blob to data URL for PDF embedding
  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  // Helper: normalize all images to JPEG data URLs for stable jsPDF rendering
  const blobToJpegDataUrl = (blob) => new Promise((resolve, reject) => {
    const tempUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0);
        const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.92);
        URL.revokeObjectURL(tempUrl);
        resolve(jpegDataUrl);
      } catch (err) {
        URL.revokeObjectURL(tempUrl);
        reject(err);
      }
    };

    image.onerror = (err) => {
      URL.revokeObjectURL(tempUrl);
      reject(err);
    };

    image.src = tempUrl;
  });

  const loadImageAsBase64 = async (image) => {
    try {
      const endpoint = `/garments/images/${image.image_id}/bytes`;

      const thumbnailResp = await api.get(endpoint, {
        params: { variant: 'thumbnail' },
        responseType: 'blob',
      });

      return await blobToJpegDataUrl(thumbnailResp.data);
    } catch (thumbnailError) {
      try {
        const fullResp = await api.get(`/garments/images/${image.image_id}/bytes`, {
          params: { variant: 'image' },
          responseType: 'blob',
        });
        return await blobToJpegDataUrl(fullResp.data);
      } catch (proxyError) {
        try {
          const fallbackUrl = image.thumbnail_url || image.image_url;
          if (!fallbackUrl) {
            return null;
          }
          const fallbackResp = await fetch(fallbackUrl);
          if (!fallbackResp.ok) {
            return null;
          }
          const fallbackBlob = await fallbackResp.blob();
          return await blobToDataUrl(fallbackBlob);
        } catch (fallbackError) {
          console.error('Failed to load image from proxy and fallback URL:', {
            image_id: image.image_id,
            thumbnailError,
            proxyError,
            fallbackError,
          });
          return null;
        }
      }
    }
  };
  
  // Pre-load all reference images as base64 IN PARALLEL
  const imageDataMap = {};
  const imagePromises = referenceImages.map(async (img) => {
    if (img?.image_id) {
      try {
        const base64 = await loadImageAsBase64(img);
        if (base64) {
          return { id: img.image_id, data: base64 };
        }
      } catch (e) {
        console.log('Failed to load image:', img.image_id);
      }
    }
    return null;
  });
  
  const loadedImages = await Promise.all(imagePromises);
  loadedImages.forEach(result => {
    if (result) {
      imageDataMap[result.id] = result.data;
    }
  });

  // Helper: Safe text function to prevent null/undefined errors
  const safeText = (text) => String(text || '') || '—';

  // ========== PAGE 1: Design Specifications + Reference Images ==========

  const customizations = lineItem?.customizations || [];
  const snapshotCategories = lineItem?.snapshot_customization_schema?.categories || [];

  const optionsMap = {};
  snapshotCategories.forEach((cat) => {
    cat.options?.forEach((opt) => {
      optionsMap[opt.option_id] = {
        ...opt,
        category_id: cat.category_id,
        category_name: cat.name,
        category_display_order: cat.display_order || 0,
        display_order: opt.display_order || 0,
      };
    });
  });

  const sortedCategories = [...snapshotCategories].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const groupedRows = {};
  sortedCategories.forEach((cat) => {
    groupedRows[cat.category_id] = [];
  });

  const fallbackRows = [];

  const hasMeaningfulCustomizationValue = (value) => {
    if (value === null || value === undefined) return false;
    const normalized = String(value).trim();
    return normalized.length > 0;
  };

  customizations.forEach((cust) => {
    if (!hasMeaningfulCustomizationValue(cust.selected_value)) {
      return;
    }

    const option = optionsMap[cust.option_id];
    if (option) {
      const categoryId = option.category_id;
      if (!groupedRows[categoryId]) {
        groupedRows[categoryId] = [];
      }
      groupedRows[categoryId].push({
        label: option.name || option.option_name || cust.option_name || 'Option',
        value: String(cust.selected_value).trim(),
        categoryId,
        display_order: option.display_order || 0,
      });
      return;
    }

    fallbackRows.push({
      label: cust.option_name || 'Option',
      value: String(cust.selected_value).trim(),
      categoryId: '__fallback__',
      display_order: 9999,
    });
  });

  const designRows = [];
  sortedCategories.forEach((cat) => {
    const options = (groupedRows[cat.category_id] || []).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    options.forEach((row) => designRows.push(row));
  });
  fallbackRows.forEach((row) => designRows.push(row));

  if (designRows.length === 0) {
    customizations.forEach((cust) => {
      if (!hasMeaningfulCustomizationValue(cust.selected_value)) {
        return;
      }

      designRows.push({
        label: cust.option_name || 'Option',
        value: String(cust.selected_value).trim(),
        categoryId: '__fallback__',
      });
    });
  }

  const specColWidth = contentWidth;
  const labelX = margin;

  // Start from larger detail font and reduce by 0.5 only when overflow happens
  const layoutProfiles = [];
  for (let detailsSize = 17; detailsSize >= 10; detailsSize -= 0.5) {
    layoutProfiles.push({
      headerTitle: Math.min(12.6, Math.max(10.8, detailsSize - 3.8)),
      headerPrimary: Math.min(16.8, Math.max(13.2, detailsSize - 0.2)),
      headerMeta: Math.min(12.2, Math.max(10.4, detailsSize - 4.2)),
      detailsSize,
      rowLineHeight: Number((detailsSize * 0.39).toFixed(2)),
      rowGap: Number((Math.max(0.22, detailsSize * 0.02)).toFixed(2)),
      categoryGap: Number((Math.max(1.15, detailsSize * 0.13)).toFixed(2)),
      imageSectionTitle: Math.max(9.8, detailsSize - 5.6),
      imageCaption: Math.max(7.4, detailsSize - 8.3),
      imageMaxHeight: detailsSize >= 14 ? 54 : detailsSize >= 12 ? 52 : 50,
      imageGap: 6,
    });
  }

  const getColumnLayout = (profile) => {
    doc.setFont('times', 'bold');
    doc.setFontSize(profile.detailsSize);
    const measuredLabelWidth = designRows.reduce((maxWidth, row) => {
      const width = doc.getTextWidth(`${safeText(row.label)}:`);
      return Math.max(maxWidth, width);
    }, 0);

    const dynamicLabelWidth = Math.min(specColWidth * 0.58, Math.max(56, measuredLabelWidth + 3));
    const dynamicGap = Math.max(4.4, Math.min(6.2, profile.detailsSize * 0.28));
    const dynamicValueX = labelX + dynamicLabelWidth + dynamicGap;
    const dynamicValueWidth = Math.max(16, specColWidth - (dynamicValueX - margin));

    return {
      labelWidth: dynamicLabelWidth,
      gap: dynamicGap,
      valueX: dynamicValueX,
      valueWidth: dynamicValueWidth,
    };
  };

  const estimateRowsEndY = (profile, startY) => {
    let y = startY;
    doc.setFont('times', 'normal');
    doc.setFontSize(profile.detailsSize);
    const cols = getColumnLayout(profile);

    let previousCategory = null;
    designRows.forEach((row) => {
      const valueLines = doc.splitTextToSize(safeText(row.value), cols.valueWidth);
      const dims = doc.getTextDimensions(valueLines);
      const textHeight = dims?.h || profile.rowLineHeight;
      const categoryGap = previousCategory && row.categoryId !== previousCategory ? profile.categoryGap : 0;
      y += categoryGap;
      y += Math.max(profile.rowLineHeight, textHeight) + profile.rowGap;
      previousCategory = row.categoryId;
    });

    return y;
  };

  let activeProfile = layoutProfiles[0];

  // Helper function to add header to each page
  const addHeader = () => {
    const headerStartY = 5.2;
    const storeName = formatStoreName(order?.store) || 'Store';

    const orderNo = garment?.order_number || '';
    const garmentSuffix = extractGarmentSuffix(garment?.garment_no, orderNo);
    const displayGarmentNo = `${orderNo}-${garmentSuffix}`;
    const productType = product?.name || lineItem?.product_name || garment?.garment_type || 'N/A';

    const topLineY = headerStartY + 4.4;
    const secondLineY = headerStartY + 9.2;

    doc.setFont('times', 'bold');
    doc.setFontSize(activeProfile.headerPrimary);
    doc.text(`${displayGarmentNo}`, margin, topLineY);

    doc.setFont('times', 'normal');
    doc.setFontSize(activeProfile.headerMeta);
    doc.text(productType, pageWidth - margin, topLineY, { align: 'right' });

    const customerName = customer?.customer_name || 'N/A';
    const customerNo = customer?.customer_no || '—';
    const patternNo = garment?.pattern_no || patternRevision?.pattern_code || 'N/A';

    doc.text(`${customerName} (${customerNo})`, margin, secondLineY);
    doc.text(`Pattern: ${patternNo}`, pageWidth - margin, secondLineY, { align: 'right' });

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.45);
    doc.line(margin, headerStartY + 11.8, pageWidth - margin, headerStartY + 11.8);

    // Extra breathing space after divider before first design row
    return headerStartY + 19.8;
  };

  for (const profile of layoutProfiles) {
    activeProfile = profile;
    const testStartY = 22.6;
    const estimatedRowsEnd = estimateRowsEndY(profile, testStartY);
    if (estimatedRowsEnd <= pageHeight - margin - 2) {
      break;
    }
  }

  currentY = addHeader();
  let specY = currentY;
  let imageY = currentY;

  const renderDesignRows = (startY, rows, startIndex = 0, previousCategory = null) => {
    let y = startY;
    let index = startIndex;
    let lastCategory = previousCategory;
    const cols = getColumnLayout(activeProfile);

    doc.setFontSize(activeProfile.detailsSize);

    while (index < rows.length) {
      const row = rows[index];
      const valueLines = doc.splitTextToSize(safeText(row.value), cols.valueWidth);
      const dims = doc.getTextDimensions(valueLines);
      const textHeight = dims?.h || activeProfile.rowLineHeight;
      const categoryGap = lastCategory && row.categoryId !== lastCategory ? activeProfile.categoryGap : 0;
      const rowHeight = Math.max(activeProfile.rowLineHeight, textHeight) + activeProfile.rowGap;
      const projectedHeight = categoryGap + rowHeight;

      if (y + projectedHeight > pageHeight - margin - 2) {
        break;
      }

      y += categoryGap;

      doc.setFont('times', 'bold');
      const label = `${safeText(row.label)}:`;
      doc.text(label, labelX, y);

      doc.setFont('times', 'normal');
      doc.text(valueLines, cols.valueX, y);

      y += rowHeight;
      lastCategory = row.categoryId;
      index += 1;
    }

    return { endY: y, nextIndex: index, lastCategory };
  };

  if (designRows.length > 0) {
    const pageOneRender = renderDesignRows(specY, designRows, 0, null);
    specY = pageOneRender.endY;

    if (pageOneRender.nextIndex < designRows.length) {
      doc.addPage();
      currentY = addHeader();
      let cursor = currentY;
      let nextIndex = pageOneRender.nextIndex;
      let carryCategory = pageOneRender.lastCategory;

      while (nextIndex < designRows.length) {
        const block = renderDesignRows(cursor, designRows, nextIndex, carryCategory);
        nextIndex = block.nextIndex;
        cursor = block.endY;
        carryCategory = block.lastCategory;
        if (nextIndex < designRows.length) {
          doc.addPage();
          cursor = addHeader();
        }
      }
    }
  } else {
    doc.setFont('times', 'normal');
    doc.setFontSize(activeProfile.detailsSize);
    doc.setTextColor(110, 110, 110);
    doc.text('No customizations specified', margin, specY);
    doc.setTextColor(0, 0, 0);
    specY += 9;
  }

  // Reference images intentionally moved to dedicated page to maximize design-table width on page 1
  
  // ========== PAGE 2: Measurements Table ==========
  
  // Always start measurements on a new page
  doc.addPage();
  currentY = addHeader();
  
  // Measurements header
  const measurementUnit = linkedMeasurement?.unit || 'inches';
  doc.setFont('times', 'bold');
  doc.setFontSize(Math.max(11, activeProfile.headerMeta));
  doc.text(`Measurements (${measurementUnit})`, margin, currentY);
  
  // Underline
  const measHeaderWidth = doc.getTextWidth(`Measurements (${measurementUnit})`);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(margin, currentY + 1, margin + measHeaderWidth, currentY + 1);
  
  currentY += 8;
  
  // Build measurements table data
  // IMPORTANT: Use CURRENT pattern data (not snapshot)
  // This ensures PDFs always show the latest pattern measurements
  const garmentSpecs = patternRevision?.garment_specifications || {};
  const hasGarmentSpecs = Object.keys(garmentSpecs).length > 0;
  
  // Filter fields that should be included in measurements table
  // Include both numeric + text measurement fields when include_in_pdf=true
  const pdfFields = specificationFields
    .filter(f => {
      if (!f?.include_in_pdf) return false;
      const fieldType = String(f?.field_type || '').toLowerCase();
      return fieldType === 'number' || fieldType === 'text';
    })
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  
  const tableData = [];
  
  // If we have pattern specifications, show them alongside body measurements
  if (hasGarmentSpecs && pdfFields.length > 0) {
    pdfFields.forEach(field => {
      const bodyValue = field.linked_measurement_field && linkedMeasurement
        ? linkedMeasurement[field.linked_measurement_field]
        : null;
      const garmentValue = garmentSpecs[field.field_key];
      
      // Format value with unit
      const formatValue = (val) => {
        if (val === null || val === undefined || val === '') return '';
        return field.unit_display ? `${val} ${measurementUnit}` : String(val);
      };
      
      tableData.push([
        field.field_label || '',
        formatValue(bodyValue),
        formatValue(garmentValue),
        '', // QC column (empty checkbox area)
      ]);
    });
  }
  
  // If no specification fields, use body measurements directly
  if (tableData.length === 0 && linkedMeasurement) {
    const measurementLabels = {
      neck_circumference: 'Neck',
      chest_circumference: 'Chest',
      upper_waist_circumference: 'Upper Waist',
      belly_circumference: 'Belly',
      upper_seat_hip_circumference: 'Upper Seat/Hip',
      shoulder: 'Shoulder',
      across_front: 'Across Front',
      across_back: 'Across Back',
      front_length_upper: 'Front Length',
      back_length_upper: 'Back Length',
      biceps_circumference: 'Biceps',
      elbow_circumference: 'Elbow',
      sleeve_length: 'Sleeve Length',
      left_wrist_circumference: 'Left Wrist',
      right_wrist_circumference: 'Right Wrist',
      lower_waist_circumference: 'Lower Waist',
      lower_seat_hip_circumference: 'Lower Seat/Hip',
      front_rise: 'Front Rise',
      u_measurement: 'U-Measure',
      thigh_circumference: 'Thigh',
      knee_circumference: 'Knee',
      outseam: 'Outseam',
    };
    
    Object.entries(measurementLabels).forEach(([key, label]) => {
      const val = linkedMeasurement[key];
      if (val !== null && val !== undefined && val !== '') {
        tableData.push([
          label,
          `${val} ${measurementUnit}`,
          '',
          '',
        ]);
      }
    });
  }
  
  if (tableData.length > 0) {
    autoTable(doc, {
      startY: currentY,
      head: [['Specification', 'Body', 'Garment', 'QC']],
      body: tableData,
      theme: 'grid',
      styles: {
        font: 'times',
        fontSize: 10.6,
        cellPadding: 2.6,
        lineColor: [0, 0, 0],
        lineWidth: 0.18,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        font: 'times',
        fontStyle: 'bold',
        halign: 'center',  // Center align headers
      },
      bodyStyles: {
        font: 'times',
        textColor: [0, 0, 0],  // Black text for body cells
      },
      columnStyles: {
        0: { cellWidth: 68, halign: 'left' },  // Specification
        1: { cellWidth: 42, halign: 'center', textColor: [0, 0, 0] },  // Body
        2: { cellWidth: 42, halign: 'center', textColor: [0, 0, 0] },  // Garment
        3: { cellWidth: 42, halign: 'center', textColor: [0, 0, 0] },  // QC
      },
      tableWidth: 'auto',
      margin: { left: margin, right: margin },
    });
    
    currentY = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFont('times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text('No measurement data available', margin, currentY);
    doc.setTextColor(0, 0, 0);
    currentY += 15;
  }

  // Guard when table is not rendered (e.g., no pattern + no linked measurements)
  // In that case `doc.lastAutoTable` is undefined, so keep current cursor position safely.
  currentY = (doc.lastAutoTable?.finalY || currentY) + 8;
  
  // Style options from pattern specifications (select type fields only)
  const styleFields = specificationFields
    .filter(f => f.include_in_pdf && f.field_type === 'select')
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  
  if (styleFields.length > 0 && Object.keys(garmentSpecs).length > 0) {
    // Check if we need space
    if (currentY > pageHeight - 50) {
      doc.addPage();
      currentY = addHeader();
    }
    
    doc.setFont('times', 'bold');
    doc.setFontSize(11);
    doc.text('Pattern Style Options', margin, currentY);
    
    const styleHeaderWidth = doc.getTextWidth('Pattern Style Options');
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(margin, currentY + 1, margin + styleHeaderWidth, currentY + 1);
    
    currentY += 6;
    
    doc.setFont('times', 'normal');
    doc.setFontSize(10.2);
    
    styleFields.forEach(field => {
      const value = garmentSpecs[field.field_key];
      if (value) {
        // Empty checkbox (for QC staff to tick)
        doc.setDrawColor(100, 100, 100);
        doc.setLineWidth(0.2);
        doc.rect(margin, currentY - 3, 3, 3);
        
        doc.text(`${safeText(field.field_label)}: ${safeText(value)}`, margin + 5, currentY);
        currentY += 5;
      }
    });
  }
  
  // ========== Reference Images (dedicated page) ==========
  
  const remainingImages = referenceImages;
  
  if (remainingImages.length > 0) {
    doc.addPage();
    currentY = addHeader();

    currentY += 6;

    // Strict 2-column x 2-row grid (4 images per page)
    const imagesPerPage = 4;
    const imagePageMargin = 6;
    const colGap = 6;
    const rowGap = 8;
    const captionHeight = 5;
    const cellPadding = 2;

    const renderImagePageHeader = () => {
      doc.setFont('times', 'bold');
      doc.setFontSize(activeProfile.imageSectionTitle);
      doc.text('Reference Images', margin, currentY);
      const headerWidth = doc.getTextWidth('Reference Images');
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.line(margin, currentY + 1, margin + headerWidth, currentY + 1);
    };

    const drawImageCell = (img, slotIndex) => {
      const row = Math.floor(slotIndex / 2);
      const col = slotIndex % 2;

      const gridTop = currentY + 6;
      const gridWidth = pageWidth - imagePageMargin * 2;
      const gridHeight = pageHeight - margin - gridTop;
      const cellWidth = (gridWidth - colGap) / 2;
      const cellHeight = (gridHeight - rowGap) / 2;

      const cellX = imagePageMargin + col * (cellWidth + colGap);
      const cellY = gridTop + row * (cellHeight + rowGap);

      doc.setDrawColor(210, 210, 210);
      doc.setLineWidth(0.2);
      doc.rect(cellX, cellY, cellWidth, cellHeight);

      const imgData = imageDataMap[img.image_id];
      const availableW = cellWidth - cellPadding * 2;
      const availableH = cellHeight - cellPadding * 2 - (img.description ? captionHeight : 0);

      if (imgData) {
        try {
          const imgProps = doc.getImageProperties(imgData);
          const aspectRatio = imgProps.width / imgProps.height;

          let drawWidth = availableW;
          let drawHeight = drawWidth / aspectRatio;

          if (drawHeight > availableH) {
            drawHeight = availableH;
            drawWidth = drawHeight * aspectRatio;
          }

          const xOffset = (availableW - drawWidth) / 2;
          const yOffset = (availableH - drawHeight) / 2;

          doc.addImage(
            imgData,
            'JPEG',
            cellX + cellPadding + xOffset,
            cellY + cellPadding + yOffset,
            drawWidth,
            drawHeight,
            undefined,
            'MEDIUM',
          );
        } catch (e) {
          doc.setFillColor(248, 248, 248);
          doc.rect(cellX + cellPadding, cellY + cellPadding, availableW, availableH, 'F');
          doc.setFontSize(9);
          doc.setTextColor(140, 140, 140);
          doc.text('Image', cellX + cellWidth / 2, cellY + cellHeight / 2, { align: 'center' });
          doc.setTextColor(0, 0, 0);
        }
      } else {
        doc.setFillColor(248, 248, 248);
        doc.rect(cellX + cellPadding, cellY + cellPadding, availableW, availableH, 'F');
        doc.setFontSize(9);
        doc.setTextColor(140, 140, 140);
        doc.text('Image', cellX + cellWidth / 2, cellY + cellHeight / 2, { align: 'center' });
        doc.setTextColor(0, 0, 0);
      }

      if (img.description) {
        doc.setFontSize(8);
        doc.setFont('times', 'italic');
        const truncatedDesc = img.description.length > 45 ? `${img.description.substring(0, 42)}...` : img.description;
        doc.text(truncatedDesc, cellX + cellWidth / 2, cellY + cellHeight - 1.5, { align: 'center' });
        doc.setFont('times', 'normal');
      }
    };

    renderImagePageHeader();

    for (let i = 0; i < remainingImages.length; i += 1) {
      if (i > 0 && i % imagesPerPage === 0) {
        doc.addPage();
        currentY = addHeader();
        renderImagePageHeader();
      }

      const slotIndex = i % imagesPerPage;
      drawImageCell(remainingImages[i], slotIndex);
    }
  }

  // Save the PDF - Add hyphen between order number and garment suffix
  const orderNo = garment?.order_number || '';
  const garmentSuffix = extractGarmentSuffix(garment?.garment_no, orderNo);
  const fileName = `OrderSheet-${orderNo}-${garmentSuffix}.pdf`;
  doc.save(fileName);
  
  return fileName;
};

/**
 * Fetches all data needed for order sheet generation and generates the PDF
 * @param {string} garmentId - The garment ID
 */
export const generateOrderSheetForGarment = async (garmentId) => {
  try {
    // Fetch garment data first (needed for subsequent calls)
    const garmentRes = await api.get(`/garments/${garmentId}`);
    const garment = garmentRes.data;
    
    // Fetch order, configs, and images IN PARALLEL
    // NOTE: We do NOT fetch categories/options because line items have snapshot_customization_schema
    const [orderRes, configsRes, imagesRes] = await Promise.all([
      api.get(`/orders/${garment.order_id}/full`),
      api.get('/garment-type-configs'),
      api.get(`/garments/${garmentId}/images`).catch(() => ({ data: [] }))
    ]);
    
    const orderData = orderRes.data;
    const referenceImages = imagesRes.data;
    
    // Extract order and customer from the response
    const order = {
      order_id: orderData.order_id,
      order_number: orderData.order_number,
      store: orderData.store,
      line_items: orderData.line_items,
      order_value: orderData.order_value,
    };
    const customer = orderData.customer;
    
    // Find the line item for this garment
    const lineItem = order.line_items?.find(li => li.line_item_id === garment.line_item_id);
    
    // Debug logging for troubleshooting
    console.log('[ORDER_SHEET_PDF] Garment:', {
      garment_id: garment.garment_id,
      garment_no: garment.garment_no,
      line_item_id: garment.line_item_id,
      is_test_garment: garment.is_test_garment,
      garment_type: garment.garment_type,
      pattern_no: garment.pattern_no
    });
    console.log('[ORDER_SHEET_PDF] Line item found:', lineItem ? 'YES' : 'NO');
    if (lineItem) {
      console.log('[ORDER_SHEET_PDF] Line item has snapshot schema:', !!lineItem.snapshot_customization_schema);
      console.log('[ORDER_SHEET_PDF] Customizations count:', lineItem.customizations?.length || 0);
    }
    
    // Determine garment type (map test types to base types)
    let garmentType = garment.garment_type || lineItem?.garment_type || 'jacket';
    if (garmentType.startsWith('test_')) {
      garmentType = garmentType.replace('test_', '');
    }
    // Normalize to lowercase for consistent pattern matching
    garmentType = garmentType.toLowerCase();
    
    // Find garment type config and fetch specification fields
    const config = configsRes.data.find(c => c.garment_type === garmentType);
    
    let specificationFields = [];
    if (config) {
      const fieldsRes = await api.get('/garment-specification-fields', {
        params: { garment_type_config_id: config.config_id }
      });
      specificationFields = fieldsRes.data;
    }
    
    // Fetch current pattern data by pattern_no (ALWAYS use latest pattern)
    let patternData = null;
    let linkedMeasurement = null;
    
    if (garment.pattern_no) {
      try {
        // Fetch all patterns for this customer and garment type
        const patternsRes = await api.get('/patterns', {
          params: { 
            customer_id: customer?.customer_id,
            garment_type: garmentType 
          }
        });
        
        console.log('[ORDER_SHEET_PDF] Fetching pattern for:', {
          pattern_no: garment.pattern_no,
          customer_id: customer?.customer_id,
          garment_type: garmentType,
          patterns_found: patternsRes.data.length
        });
        
        // Find the pattern by pattern_code
        const pattern = patternsRes.data.find(p => p.pattern_code === garment.pattern_no);
        
        if (pattern) {
          patternData = {
            pattern_code: pattern.pattern_code,
            garment_specifications: pattern.garment_specifications || {},
            linked_measurement_id: pattern.linked_measurement_id
          };
          
          console.log('[ORDER_SHEET_PDF] Pattern found:', {
            pattern_code: patternData.pattern_code,
            has_specs: Object.keys(patternData.garment_specifications).length > 0,
            specs_count: Object.keys(patternData.garment_specifications).length,
            linked_measurement_id: patternData.linked_measurement_id
          });
          
          // Fetch linked measurement if exists
          if (pattern.linked_measurement_id) {
            try {
              const measurementRes = await api.get(`/measurements/${pattern.linked_measurement_id}`);
              linkedMeasurement = measurementRes.data;
              console.log('[ORDER_SHEET_PDF] Linked measurement found');
            } catch (e) {
              console.log('[ORDER_SHEET_PDF] Linked measurement not found');
            }
          }
        } else {
          console.log('[ORDER_SHEET_PDF] Pattern not found for pattern_no:', garment.pattern_no);
        }
      } catch (e) {
        console.error('Error fetching pattern:', e);
      }
    }
    
    // If no linked measurement, try to get the latest measurement for the customer
    if (!linkedMeasurement && customer?.customer_id) {
      try {
        const measurementsRes = await api.get('/measurements', {
          params: { customer_id: customer.customer_id }
        });
        if (measurementsRes.data.length > 0) {
          linkedMeasurement = measurementsRes.data[0]; // Latest measurement
        }
      } catch (e) {
        console.log('No measurements found');
      }
    }
    
    // Generate the PDF using snapshot or current pattern data
    const fileName = await generateOrderSheetPdf({
      garment,
      order,
      customer,
      lineItem,
      product: lineItem ? { name: lineItem.product_name, type: garmentType } : null,
      allCategories: [], // Not needed - snapshot has everything
      allOptions: [],    // Not needed - snapshot has everything
      patternRevision: patternData, // Use patternData (from snapshot or current pattern)
      linkedMeasurement,
      specificationFields,
      referenceImages,
    });
    
    return fileName;
  } catch (error) {
    console.error('Error generating order sheet:', error);
    throw error;
  }
};

export default generateOrderSheetPdf;
